import { getBrowserCapabilityWarnings, canUseDom } from "../utils/browser";
import { renderPrintDocument } from "../render/renderDocument";
import { printInIframe } from "../runtime/iframeAdapter";
import { previewInIframe } from "../runtime/previewAdapter";
import { normalizeMargins, resolvePageSize } from "./units";
import type {
  BarcodeElement,
  HtmlElement,
  ImageElement,
  LengthInput,
  PageBreakElement,
  PageMargins,
  PageOrientation,
  PageSize,
  PreviewOptions,
  PrintElement,
  PrintJobOptions,
  PrintJobSnapshot,
  QrCodeElement,
  RenderedPrintDocument,
  StyleMap,
  TableColumn,
  TableElement,
  TextElement,
  ToHtmlOptions
} from "./types";

// PrintJob is the main authoring API. It stores an ordered element stream and
// page settings, then hands the snapshot to the shared render pipeline.
export class PrintJob {
  private title: string;
  private className?: string;
  private fontFamily?: string;
  private pageSize: PageSize;
  private orientation: PageOrientation;
  private margins: PageMargins;
  private readonly elements: PrintElement[] = [];

  constructor(options: PrintJobOptions = {}) {
    this.title = options.title ?? "Print Document";
    this.className = options.className;
    this.fontFamily = options.fontFamily;
    this.pageSize = resolvePageSize(options.pageSize ?? "A4");
    this.orientation = options.orientation ?? "portrait";
    this.margins = normalizeMargins(options.margins);
  }

  setTitle(title: string): this {
    this.title = title;
    return this;
  }

  setPageSize(pageSize: NonNullable<PrintJobOptions["pageSize"]>): this {
    this.pageSize = resolvePageSize(pageSize);
    return this;
  }

  setOrientation(orientation: NonNullable<PrintJobOptions["orientation"]>): this {
    this.orientation = orientation;
    return this;
  }

  setMargins(margins: NonNullable<PrintJobOptions["margins"]>): this {
    this.margins = normalizeMargins(margins);
    return this;
  }

  addText(text: string, options: Omit<TextElement, "kind" | "text"> = {}): this {
    this.elements.push({ kind: "text", text, ...options });
    return this;
  }

  addHtml(html: string, options: Omit<HtmlElement, "kind" | "html"> = {}): this {
    this.elements.push({ kind: "html", html, ...options });
    return this;
  }

  addImage(src: string, options: Omit<ImageElement, "kind" | "src"> = {}): this {
    this.elements.push({ kind: "image", src, ...options });
    return this;
  }

  // Add an explicit manual page break marker so callers do not need to encode
  // pagination using ad-hoc HTML or low-level per-element break flags.
  addPageBreak(options: Omit<PageBreakElement, "kind"> = {}): this {
    this.elements.push({ kind: "pageBreak", ...options });
    return this;
  }

  addTable<Row extends object>(
    rows: Row[],
    columns: Array<TableColumn<Row>>,
    options: Omit<TableElement<Row>, "kind" | "rows" | "columns"> = {}
  ): this {
    this.elements.push({ kind: "table", rows, columns, ...options } as TableElement);
    return this;
  }

  addBarcode(value: string, options: Omit<BarcodeElement, "kind" | "value"> = {}): this {
    this.elements.push({ kind: "barcode", value, ...options });
    return this;
  }

  addQRCode(value: string, options: Omit<QrCodeElement, "kind" | "value"> = {}): this {
    this.elements.push({ kind: "qrcode", value, ...options });
    return this;
  }

  clear(): this {
    this.elements.length = 0;
    return this;
  }

  // Surface browser limitations as data so calling applications can decide how
  // to display warnings in their own UI.
  getWarnings() {
    return getBrowserCapabilityWarnings();
  }

  // Snapshot the current job state. Preview, print, tests, and docs all rely on
  // this stable structure rather than reading mutable private fields directly.
  toJSON(): PrintJobSnapshot {
    return {
      title: this.title,
      className: this.className,
      fontFamily: this.fontFamily,
      pageSize: this.pageSize,
      orientation: this.orientation,
      margins: this.margins,
      elements: [...this.elements]
    };
  }

  // Render the current snapshot into HTML and CSS without touching the DOM.
  async render(options: ToHtmlOptions = {}): Promise<RenderedPrintDocument> {
    return renderPrintDocument(this.toJSON(), options);
  }

  async toHTML(options: ToHtmlOptions = {}): Promise<string> {
    const rendered = await this.render(options);
    return rendered.html;
  }

  // Preview writes into a visible iframe and may run a two-pass measurement flow
  // when estimated pagination is enabled.
  async preview(options: PreviewOptions): Promise<HTMLIFrameElement> {
    if (!canUseDom()) {
      throw new Error("Preview requires a browser DOM environment.");
    }

    return previewInIframe(this.toJSON(), options);
  }

  // Print always uses the hidden iframe path and intentionally skips preview-only
  // estimated pagination so the browser print engine stays authoritative.
  async print(): Promise<void> {
    if (!canUseDom()) {
      throw new Error("Print requires a browser DOM environment.");
    }

    await printInIframe(this.toJSON());
  }
}

export function createPrintJob(options?: PrintJobOptions): PrintJob {
  return new PrintJob(options);
}

export type { LengthInput, PreviewOptions, StyleMap };
